// contests.component.ts
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, OnDestroy, ViewChild, TemplateRef, HostListener, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { DataService } from '@services/contests-data.service';
import { SharedModule } from '@shared/shared.module';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HighlightPipe } from "../../../pipes/highlight.pipe";
import { v4 as uuidv4 } from 'uuid';
import { FilterModalComponent, FilterConfig } from '@pages/filter-modal/filter-modal.component';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SecureStorageService } from '@services/secure-storage.service';

interface OverviewStats {
  totalContests: number;
  activeContests: number;
  pendingContests: number;
  finishedContests: number;
  avgClickRate: number;
  avgCompletionRate: number;
  avgParticipationRate: number;
  avgEngagementRate: number;
}

@Component({
  selector: 'app-contests',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    SharedModule,
    NzPaginationModule,
    NzSelectModule,
    NzModalModule,
    NzTableModule,
    NzToolTipModule,
    NzPopconfirmModule,
    FilterModalComponent,
    HighlightPipe,
    NzDatePickerModule
  ],
  templateUrl: './contests.component.html',
  styleUrls: ['./contest.component.scss'],
})
export class ContestsComponent implements OnInit, OnDestroy {
  private routerSubscription!: Subscription;
  contest: any[] = [];
  completedPageIndex = 1;
  draftPageIndex = 1;
  filterModalVisible: boolean = false;
  pageIndex = 1;
  perPage = 5;
  totalContestLength = 0;
  totalCompletedContestLength = 0;
  totalDraftContestLength = 0;
  searchTerm: string = '';
  countryRecords: any;
  currentFilters: any = {};
  activeShareMenuIndex: number | null = null;
  activeShareId: number | null = null;
  tabs_grid: 'all_contest' | 'completed' | 'draft' = 'all_contest';
  completedContests: any[] = [];
  draftContests: any[] = [];
  // Overview variables
  overviewStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  previousStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  overviewTrends: Record<keyof OverviewStats | 'overallSuccessRate', number> = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0,
    overallSuccessRate: 0
  };
  overviewLoading: boolean = false;
  overviewDateRange: Date[] = [];
  overviewPickerOpen: boolean = false;
  private elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.overviewPickerOpen) {
      const target = event.target as HTMLElement;
      const isInsideDateArea = target.closest('.btn-date')?.parentElement
        || target.closest('.ant-picker-dropdown')
        || target.closest('.ant-picker-panel')
        || target.closest('nz-range-picker');
      if (!isInsideDateArea) {
        this.overviewPickerOpen = false;
      }
    }
  }
  overviewContestId: string = '';
  contestsList: any[] = [];
  http = inject(HttpClient);
  pagesService = inject(PagesService);
  fb = inject(FormBuilder);
  sharedService = inject(SharedService);
  secureStorage = inject(SecureStorageService);
  dataService = inject(DataService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;





  // Filter configuration for modal
  filterConfig: FilterConfig = {
    showTimeline: true,
    showLevel: true,
    showStatus: true,
    showFormat: true,
    showType: true,
    showViewType: true,
    showLocation: true,
    statusOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'ongoing', label: 'Ongoing' },
      { value: 'waiting_prize', label: 'Waiting Prize' },
      { value: 'completed', label: 'Completed' },
      { value: 'draft', label: 'Draft' }
    ],
    levelOptions: [1, 2, 3, 4, 5],
    formatOptions: [
      { value: 'integrated', label: 'Integrated' },
      { value: 'non-integrated', label: 'Non-Integrated' }
    ],
    typeOptions: [
      { value: 'private', label: 'Private' },
      { value: 'public', label: 'Public' }
    ],
    viewTypeOptions: [
      { value: 'vip', label: 'VIP' },
      { value: 'recent contest', label: 'Recent Contest' }
    ]
  };

  // Image Modal Properties
  imageModalVisible: boolean = false;
  imageModalImages: string[] = [];
  imageModalCurrentIndex: number = 0;

  // Soft Delete with Undo Properties
  pendingDeletes: Map<number, { contest: any, timer: any, listType: string, pageIndex: number }> = new Map();
  @ViewChild('undoNotificationTpl', { static: true }) undoNotificationTpl!: TemplateRef<any>;
  hasSdkAccess = false;

  constructor(private modal: NzModalService, private notificationService: NzNotificationService) { }

  ngOnInit(): void {
    this.hasSdkAccess = this.secureStorage.getItem<boolean>('hasSdkAccess') || false;
    this.getContestLists();
    this.getCountryRecords();
    this.getOverviewStats();
    this.loadContestsList();

    // Listen for route changes to delete pending items before navigation
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.deletePendingContestsImmediately();
      });
  }

  ngOnDestroy(): void {
    // Cleanup router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    // Delete any pending contests before component is destroyed
    this.deletePendingContestsImmediately();
  }

  // Delete all pending contests immediately (on route change or component destroy)
  private deletePendingContestsImmediately(): void {
    this.pendingDeletes.forEach((_, id) => {
      // Call API to delete
      const data = { contest_id: id };
      this.pagesService.update('DELETE_CONTEST', data).subscribe({
        next: () => {
          this.dataService.refreshContestData();
        },
        error: () => { }
      });
    });
    this.pendingDeletes.clear();
    this.notificationService.remove();
  }

  private buildContestListRequest(page: number, perPage: number, status: string = '', filters: any = {}): any {
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      page,
      per_page: perPage,
      status: status || '',
      format_of_contest: filters.format || '',
      type: filters.type || '',
      View_Type: filters.viewType || '',
      location: (filters.location && Array.isArray(filters.location) && filters.location.length > 0)
        ? filters.location.join(',')
        : '',
      level_expiry_start_date: (filters.dateRange && filters.dateRange.length === 2)
        ? formatDate(new Date(filters.dateRange[0]))
        : '',
      level_expiry_end_date: (filters.dateRange && filters.dateRange.length === 2)
        ? formatDate(new Date(filters.dateRange[1]))
        : '',
      number_of_levels: filters.level ? filters.level.toString() : '',
      search: this.searchTerm?.trim() || ''
    };
  }

  private getExportConfigByTab(): { status: string; reportTitle: string; fileName: string } {
    switch (this.tabs_grid) {
      case 'completed':
        return {
          status: 'completed',
          reportTitle: 'Completed Contests Report',
          fileName: 'Completed_Contests_Report'
        };
      case 'draft':
        return {
          status: 'draft',
          reportTitle: 'Draft Contests Report',
          fileName: 'Draft_Contests_Report'
        };
      default:
        return {
          status: '',
          reportTitle: 'All Contests Report',
          fileName: 'All_Contests_Report'
        };
    }
  }

  private fetchAllContestsForExport(
    status: string,
    onSuccess: (items: any[]) => void
  ): void {
    const totalCount = this.getActiveTotalCount();
    const finalStatus = this.currentFilters?.status || status || '';

    if (!totalCount || totalCount <= 0) {
      this.sharedService.createNotification('error', 'No Contests Found');
      return;
    }

    const requestBody = this.buildContestListRequest(1, totalCount, finalStatus, this.currentFilters || {});
    this.sharedService.showLoader();
    this.pagesService.getAll('TEST_PAGINATION', requestBody).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        const contestsToExport = response?.items || [];
        if (!Array.isArray(contestsToExport) || contestsToExport.length === 0) {
          this.sharedService.createNotification('error', 'No Contests Found');
          return;
        }
        onSuccess(contestsToExport);
      },
      error: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to fetch contests for export');
      }
    });
  }

  getContestLists(page: number = 1, status: string = '', filters: any = {}, showLoader: boolean = true) {
    if (showLoader) this.sharedService.showLoader();
    const now = new Date().getTime();

    const finalStatus = filters.status || status || '';
    const requestBody: any = this.buildContestListRequest(page, this.perPage, finalStatus, filters);

    this.pagesService.getAll('TEST_PAGINATION', requestBody).subscribe({
      next: (response: any) => {
        // Extract items and meta from response
        const items = response?.items || [];
        const meta = response?.meta || {};

        // Process contest items
        const processedContests = items.map((contest: any) => {
          // ✅ Clean image URLs
          if (Array.isArray(contest.banners)) {
            contest.banners = contest.banners.map((banner: any) => ({
              ...banner,
              url: this.cleanImageUrl(banner.url || banner.path || ''),
              path: this.cleanImageUrl(banner.path || banner.url || '')
            }));
          }

          if (Array.isArray(contest._contest_prize_of_contest_list)) {
            contest._contest_prize_of_contest_list = contest._contest_prize_of_contest_list.map((prize: any) => ({
              ...prize,
              image: prize.image
                ? {
                  ...prize.image,
                  url: this.cleanImageUrl(prize.image.url || prize.image.path || ''),
                  path: this.cleanImageUrl(prize.image.path || prize.image.url || '')
                }
                : null
            }));
          }

          // ✅ Mark expired
          contest.isExpired = contest.end_date
            ? new Date(contest.end_date).getTime() < now
            : false;

          return contest;
        });

        // Sort by created_at descending (newest first)
        processedContests.sort((a: any, b: any) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        });

        // Use filtered counts from backend for pagination
        // When filters are applied, use filtered_total/filtered_draft/filtered_completed
        // When no filters, use all_count/draft_count/completed_count

        if (this.tabs_grid === 'all_contest') {
          this.contest = processedContests;
          // Use filtered_total if available, otherwise fall back to all_count
          this.totalContestLength = meta?.filtered_total ?? meta?.all_count ?? items.length;
        } else if (this.tabs_grid === 'completed') {
          this.completedContests = processedContests;
          this.totalCompletedContestLength = meta?.filtered_completed ?? meta?.completed_count ?? items.length;
        } else if (this.tabs_grid === 'draft') {
          this.draftContests = processedContests;
          this.totalDraftContestLength = meta?.filtered_draft ?? meta?.draft_count ?? items.length;
        }

        if (showLoader) this.sharedService.hideLoader();
      },
      error: (error) => {
        if (showLoader) this.sharedService.hideLoader();
        this.sharedService.createNotification('error', error?.message || 'Failed to load contests');
      },
    });
  }

  private cleanImageUrl(url: string): string {
    if (!url) return '';
    if (url.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
      return url.replace('https://xi5f-5y62-kbyr.n7e.xano.iohttps://', 'https://');
    }
    return url;
  }

  // Get overview stats from API
  getOverviewStats(): void {
    const params: any = {
      contestid: this.overviewContestId || 0,
      contest_id: this.overviewContestId || 0,
      start_date: this.overviewDateRange[0] ? this.formatDateToString(this.overviewDateRange[0]) : 0,
      end_date: this.overviewDateRange[1] ? this.formatDateToString(this.overviewDateRange[1]) : 0
    };

    this.overviewLoading = true;

    this.pagesService.getAll('DASH_CONTEST_OVERVIEW', params).subscribe({
      next: (res: any) => {
        const readValue = (obj: any, fallback: number = 0): number => {
          if (obj == null) return fallback;
          if (typeof obj === 'number') return obj;
          if (typeof obj?.value === 'number') return obj.value;
          return fallback;
        };
        const readTrend = (obj: any): number | null => {
          if (obj == null) return null;
          if (typeof obj?.trend === 'number') return obj.trend;
          return null;
        };
        const prevFromTrend = (current: number, trend: number | null): number => {
          if (trend == null || !isFinite(trend)) return 0;
          const denom = 1 + trend / 100;
          if (denom === 0) return 0;
          return current / denom;
        };

        const totalContests = readValue(res?.totalContests, readValue(res?.total_contest, 0));
        const activeContests = readValue(res?.activeContests, readValue(res?.active_contest, 0));
        const pendingContests = readValue(res?.pendingContests, readValue(res?.pending_contest, 0));
        const finishedContests = readValue(res?.finishedContests, readValue(res?.finished_contest, 0));
        const overallSuccessRate = readValue(
          res?.overallSuccessRate,
          readValue(res?.averageClickThroughRate, readValue(res?.averageCTR, 0))
        );
        const avgCompletion = readValue(
          res?.avgCompletionRate,
          readValue(res?.averageCompletionRate, readValue(res?.avg_completion_rate, 0))
        );
        const avgParticipation = readValue(
          res?.avgParticipationRate,
          readValue(res?.averageParticipationRate, readValue(res?.avg_participation_rate, 0))
        );
        const avgEngagement = readValue(
          res?.avgEngagementRate,
          readValue(res?.averageEngagementRate, readValue(res?.avg_engagement_rate, 0))
        );

        const totalTrend = readTrend(res?.totalContests);
        const activeTrend = readTrend(res?.activeContests);
        const pendingTrend = readTrend(res?.pendingContests);
        const finishedTrend = readTrend(res?.finishedContests);
        const overallTrend = readTrend(res?.overallSuccessRate) ?? readTrend(res?.averageClickThroughRate);
        const completionTrend = readTrend(res?.avgCompletionRate) ?? readTrend(res?.averageCompletionRate);
        const participationTrend = readTrend(res?.avgParticipationRate) ?? readTrend(res?.averageParticipationRate);
        const engagementTrend = readTrend(res?.avgEngagementRate) ?? readTrend(res?.averageEngagementRate);

        this.overviewStats = {
          totalContests,
          activeContests,
          pendingContests,
          finishedContests,
          avgClickRate: overallSuccessRate,
          avgCompletionRate: avgCompletion,
          avgParticipationRate: avgParticipation,
          avgEngagementRate: avgEngagement
        };

        this.previousStats = {
          totalContests: prevFromTrend(totalContests, totalTrend),
          activeContests: prevFromTrend(activeContests, activeTrend),
          pendingContests: prevFromTrend(pendingContests, pendingTrend),
          finishedContests: prevFromTrend(finishedContests, finishedTrend),
          avgClickRate: prevFromTrend(overallSuccessRate, overallTrend),
          avgCompletionRate: prevFromTrend(avgCompletion, completionTrend),
          avgParticipationRate: prevFromTrend(avgParticipation, participationTrend),
          avgEngagementRate: prevFromTrend(avgEngagement, engagementTrend)
        };

        this.overviewTrends = {
          totalContests: totalTrend ?? 0,
          activeContests: activeTrend ?? 0,
          pendingContests: pendingTrend ?? 0,
          finishedContests: finishedTrend ?? 0,
          avgClickRate: overallTrend ?? 0,
          avgCompletionRate: completionTrend ?? 0,
          avgParticipationRate: participationTrend ?? 0,
          avgEngagementRate: engagementTrend ?? 0,
          overallSuccessRate: overallTrend ?? 0
        };
        this.overviewLoading = false;
      },
      error: (error) => {
        console.error('Failed to load overview stats:', error);
        this.overviewLoading = false;
      }
    });
  }

  // Open filter modal
  openFilterModal(): void {
    this.filterModalVisible = true;
  }
  // Handle filters applied from modal
  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    // Reset to page 1 when filters change
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API with filters
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed', filters);
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft', filters);
    } else {
      this.getContestLists(1, '', filters);
    }
  }
  // Handle filter reset from modal
  onFiltersReset(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    // Reset to page 1
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API without filters
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed');
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft');
    } else {
      this.getContestLists(1, '');
    }
  }

  // Helper method to check if any filters are active
  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }
  // Apply search (called on search input change) - no loader
  applySearch(): void {
    // Reset to page 1 when search changes
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API with current filters + search term (no loader for search)
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed', this.currentFilters, false);
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft', this.currentFilters, false);
    } else {
      this.getContestLists(1, '', this.currentFilters, false);
    }
  }
  // Clear all filters and reset
  clearAllFilters(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    // Reset the filter modal form
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        timeline: null,
        level: null,
        status: null,
        format: null,
        type: null,
        viewType: null,
        location: []
      });
    }
    // Call API without filters
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed');
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft');
    } else {
      this.getContestLists(1, '');
    }
  }

  // Helper: Format Date to DD-MM-YYYY string
  formatDateToString(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  loadContestsList(): void {
    this.pagesService.getAll('GET_DASHBOARD_CONTESTS', { contest_id: 0 }).subscribe({
      next: (response: any) => {
        this.contestsList = response?.items || response?.data || response || [];
      },
      error: () => {
        this.contestsList = [];
      }
    });
  }

  onOverviewContestChange(contestId: string): void {
    this.overviewContestId = contestId || '';
    this.getOverviewStats();
  }

  onOverviewDateChange(dates: Date[]): void {
    this.overviewDateRange = dates || [];
    this.getOverviewStats();
  }
  getTimeCategory(date: string): 'past' | 'ongoing' | 'upcoming' | 'expired' {
    const now = new Date();
    const endDate = new Date(date);

    if (isNaN(endDate.getTime())) return 'expired';
    if (endDate.getTime() < now.getTime()) return 'expired';

    const diffHours = Math.abs(endDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (diffHours <= 12) return 'ongoing';

    return 'upcoming';
  }
  get pendingContests() {
    return this.contest.filter(
      (x) => (x.approval_status?.toLowerCase() ?? '') === 'pending'
    );
  }
  get pagedAllContests() {
    // Server-side pagination: return all items as backend already paginated
    return this.contest || [];
  }

  get pagedDraftContests() {
    // Server-side pagination: return all items as backend already paginated
    return this.draftContests || [];
  }
  getPageRangeText(list: any[], pageIndex: number): string {
    // For server-side pagination, calculate based on per_page and current page
    let total = 0;
    if (this.tabs_grid === 'completed') {
      total = this.totalCompletedContestLength;
    } else if (this.tabs_grid === 'draft') {
      total = this.totalDraftContestLength;
    } else {
      total = this.totalContestLength;
    }

    const start = total === 0 ? 0 : (pageIndex - 1) * this.perPage + 1;
    const end = Math.min(pageIndex * this.perPage, total);
    return `${start}-${end} of ${total} items`;
  }

  getPageRangeTextTotal(total: number, pageIndex: number): string {
    const start = total === 0 ? 0 : (pageIndex - 1) * this.perPage + 1;
    const end = Math.min(pageIndex * this.perPage, total);
    return `${start}-${end} of ${total} items`;
  }
  editContest(id: number) {
    const uid = uuidv4();
    localStorage.setItem(uid, id.toString());
    this.router.navigate(['/home/contests/edit'], { queryParams: { id: uid } });
  }
  // Soft delete with undo functionality
  softDeleteContest(id: number) {
    // Find the contest in the appropriate list
    let contest: any = null;
    let listType = '';

    if (this.tabs_grid === 'all_contest') {
      contest = this.contest.find(c => c.id === id);
      listType = 'all';
    } else if (this.tabs_grid === 'completed') {
      contest = this.completedContests.find(c => c.id === id);
      listType = 'completed';
    } else if (this.tabs_grid === 'draft') {
      contest = this.draftContests.find(c => c.id === id);
      listType = 'draft';
    }

    if (!contest) return;

    // Store current page index before deletion
    let currentPageIndex = this.pageIndex;
    if (listType === 'completed') {
      currentPageIndex = this.completedPageIndex;
    } else if (listType === 'draft') {
      currentPageIndex = this.draftPageIndex;
    }

    // Remove from display list immediately (just visually hide it)
    if (listType === 'all') {
      this.contest = this.contest.filter(c => c.id !== id);
      this.totalContestLength--;
    } else if (listType === 'completed') {
      this.completedContests = this.completedContests.filter(c => c.id !== id);
      this.totalCompletedContestLength--;
    } else if (listType === 'draft') {
      this.draftContests = this.draftContests.filter(c => c.id !== id);
      this.totalDraftContestLength--;
    }

    // Store pending delete info with page index for undo
    this.pendingDeletes.set(id, { contest, timer: null, listType, pageIndex: currentPageIndex });

    // Show notification with undo button
    const notificationRef = this.notificationService.template(this.undoNotificationTpl, {
      nzData: { id, contestName: contest.name },
      nzDuration: 3000,
      nzPlacement: 'topRight',
      nzClass: 'undo-notification',
      nzKey: `delete-${id}`,
      nzPauseOnHover: true
    });

    // When notification closes (timeout or manually), trigger permanent delete
    notificationRef.onClose.subscribe(() => {
      // Small delay to allow undo click to process first
      setTimeout(() => {
        if (this.pendingDeletes.has(id)) {
          this.permanentDeleteContest(id);
        }
      }, 100);
    });
  }

  // Undo delete - restore contest to list
  undoDelete(id: number) {
    const pendingDelete = this.pendingDeletes.get(id);
    if (!pendingDelete) return;

    // Remove from pending deletes first to prevent double processing
    this.pendingDeletes.delete(id);

    // Close specific notification
    this.notificationService.remove(`delete-${id}`);

    // Restore to the appropriate list and refresh with correct page
    if (pendingDelete.listType === 'all') {
      this.totalContestLength++;
      this.pageIndex = pendingDelete.pageIndex;
      this.getContestLists(this.pageIndex, '', this.currentFilters, false);
    } else if (pendingDelete.listType === 'completed') {
      this.totalCompletedContestLength++;
      this.completedPageIndex = pendingDelete.pageIndex;
      this.getContestLists(this.completedPageIndex, 'completed', this.currentFilters, false);
    } else if (pendingDelete.listType === 'draft') {
      this.totalDraftContestLength++;
      this.draftPageIndex = pendingDelete.pageIndex;
      this.getContestLists(this.draftPageIndex, 'draft', this.currentFilters, false);
    }

    this.sharedService.createNotification('success', 'Contest restored successfully!');
  }

  // Permanent delete - called after 10 seconds if not undone
  permanentDeleteContest(id: number) {
    const pendingDelete = this.pendingDeletes.get(id);
    if (!pendingDelete) return;

    // Remove from pending deletes
    this.pendingDeletes.delete(id);

    // Call API to delete from database
    const data = { contest_id: id };
    this.pagesService.update('DELETE_CONTEST', data).subscribe({
      next: () => {
        this.getOverviewStats();
        this.dataService.refreshContestData();

        // Refresh list to get correct number of records per page
        if (pendingDelete.listType === 'all') {
          // Check if we need to go back a page (if current page is now empty)
          const maxPage = Math.ceil(this.totalContestLength / this.perPage) || 1;
          if (this.pageIndex > maxPage) {
            this.pageIndex = maxPage;
          }
          this.getContestLists(this.pageIndex, '', this.currentFilters, false);
        } else if (pendingDelete.listType === 'completed') {
          const maxPage = Math.ceil(this.totalCompletedContestLength / this.perPage) || 1;
          if (this.completedPageIndex > maxPage) {
            this.completedPageIndex = maxPage;
          }
          this.getContestLists(this.completedPageIndex, 'completed', this.currentFilters, false);
        } else if (pendingDelete.listType === 'draft') {
          const maxPage = Math.ceil(this.totalDraftContestLength / this.perPage) || 1;
          if (this.draftPageIndex > maxPage) {
            this.draftPageIndex = maxPage;
          }
          this.getContestLists(this.draftPageIndex, 'draft', this.currentFilters, false);
        }
      },
      error: () => {
        // If delete fails, restore by refreshing the list
        if (pendingDelete.listType === 'all') {
          this.totalContestLength++;
          this.getContestLists(pendingDelete.pageIndex, '', this.currentFilters, false);
        } else if (pendingDelete.listType === 'completed') {
          this.totalCompletedContestLength++;
          this.getContestLists(pendingDelete.pageIndex, 'completed', this.currentFilters, false);
        } else if (pendingDelete.listType === 'draft') {
          this.totalDraftContestLength++;
          this.getContestLists(pendingDelete.pageIndex, 'draft', this.currentFilters, false);
        }
        this.sharedService.createNotification('error', 'Failed to delete contest');
      },
    });
  }

  // Legacy delete method (keeping for reference)
  deleteContest(id: number) {
    this.sharedService.showLoader();
    const data = { contest_id: id };
    this.pagesService.update('DELETE_CONTEST', data).subscribe({
      next: (response) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('success', 'Contest deleted successfully!');
        this.getContestLists();
        this.getOverviewStats();
        this.dataService.refreshContestData();
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(error, error);
      },
    });
  }
  showContestDetail(contest: any) {
    const { id } = contest;
    const uid = uuidv4();
    localStorage.setItem(uid, id.toString());
    this.router.navigate(['/home/contests/detail'], { queryParams: { id: uid } });
  }

  showParticipantDetail(item: any) {
    const contestId = item?.id; // item me se contest ki ID nikal lo
    if (contestId) {
      this.router.navigate(['/home/participant'], {
        queryParams: { id: contestId } // id ko param me pass kar diya
      });
    } else {
      console.error('Contest ID not found!');
    }
  }

  viewParticipants(contestId: number, contestName?: string) {
    if (contestId) {
      this.router.navigate(['/home/participants'], {
        queryParams: { contest_id: contestId, contest_name: contestName || '' }
      });
    }
  }

  getTimeRemaining(createdAt: string) {
    return this.sharedService.timeRemaining(createdAt);
  }
  onPageSizeChange(size: number): void {
    this.perPage = size;
    // Reset to page 1 when page size changes
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Reload data with new page size and current filters
    if (this.tabs_grid === 'completed') {
      this.getContestLists(1, 'completed', this.currentFilters);
    } else if (this.tabs_grid === 'draft') {
      this.getContestLists(1, 'draft', this.currentFilters);
    } else {
      this.getContestLists(1, '', this.currentFilters);
    }
  }

  onPageIndexChange(index: number): void {
    if (this.tabs_grid === 'completed') {
      this.completedPageIndex = index;
      this.getContestLists(index, 'completed', this.currentFilters);
    } else if (this.tabs_grid === 'draft') {
      this.draftPageIndex = index;
      this.getContestLists(index, 'draft', this.currentFilters);
    } else {
      this.pageIndex = index;
      this.getContestLists(index, '', this.currentFilters);
    }
  }

  // getter for paged completed contests
  get pagedCompletedContests() {
    // Server-side pagination: return all items as backend already paginated
    return this.completedContests || [];
  }

  // Add method to get correct page range text based on active tab
  getActivePageRangeText(): string {
    if (this.tabs_grid === 'completed') {
      return this.getPageRangeText(this.completedContests, this.completedPageIndex);
    }
    return this.getPageRangeText(this.contest, this.pageIndex);
  }


  getStatusClass(status: string): string {
    switch (status) {
      case 'draft': return 'bg-secondary';
      case 'pending': return 'bg-warning';
      case 'approved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      case 'completed': return 'bg-success';
      case 'ongoing': return 'bg-info';
      case 'waiting_prize': return 'bg-purple';
      default: return 'bg-dark';
    }
  }

  // Calculate dynamic display status based on contest state
  getDisplayStatus(item: any): string {
    const now = new Date();
    const status = (item.status || '').toLowerCase();

    // If status is 'completed' (prize announced from backend), return as-is
    if (status === 'completed') {
      return 'completed';
    }

    // If status is not approved, return as-is (draft, pending, rejected)
    if (status !== 'approved') {
      return status;
    }

    // Contest is approved but not active yet - waiting to be published
    if (item.active !== true) {
      return 'waiting_publish';
    }

    // Contest is approved AND active - check dates to determine ongoing/waiting_prize
    const startDate = item.start_date ? new Date(item.start_date) : null;
    const endDate = item.end_date ? new Date(item.end_date) : null;

    // If contest has started but not ended yet -> Ongoing
    if (startDate && endDate && now >= startDate && now < endDate) {
      return 'ongoing';
    }

    // If contest has ended but prize not announced yet -> Waiting Prize
    // completed status comes from backend when prize is announced
    if (endDate && now >= endDate) {
      return 'waiting_prize';
    }

    // Contest approved and active but not started yet
    return 'approved';
  }

  // Get display label for status
  getStatusLabel(status: string): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'pending': return 'Pending';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'completed': return 'Completed';
      case 'ongoing': return 'Ongoing';
      case 'waiting_prize': return 'Waiting Prize';
      case 'waiting_publish': return 'Waiting for Publish';
      default: return status || '-';
    }
  }

  getCountryRecords() {
    this.sharedService.getCountryRecords().then((data) => {
      this.countryRecords = data;
    });
  }

  // / Add method to get correct total count
  getActiveTotalCount(): number {
    if (this.tabs_grid === 'completed') {
      return this.totalCompletedContestLength;
    } else if (this.tabs_grid === 'draft') {
      return this.totalDraftContestLength;
    }
    return this.totalContestLength;
  }

  onCurrentPageDataChange(data: readonly any[]): void {
    // Not needed for server-side pagination, but kept for template compatibility
  }

  onCompletedPageIndexChange(index: number): void {
    this.completedPageIndex = index;
    this.getContestLists(index, 'completed', this.currentFilters);
  }

  onDraftPageIndexChange(index: number): void {
    this.draftPageIndex = index;
    this.getContestLists(index, 'draft', this.currentFilters);
  }

  // Add method to get correct page index
  getActivePageIndex(): number {
    if (this.tabs_grid === 'completed') {
      return this.completedPageIndex;
    }
    return this.pageIndex;
  }

  logSomething(record: any) { }

  cancel(): void { }

  confirmDeleteContest(id: number, contestName?: string): void {
    const safeName = contestName || 'this contest';
    this.modal.confirm({
      nzTitle: 'Delete Contest',
      nzContent: `Are you sure you want to delete "${safeName}"?`,
      nzOkText: 'Delete',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzCentered: true,
      nzClassName: 'contest-styled-modal',
      nzOnOk: () => this.softDeleteContest(id),
    });
  }

  getPrizeNames(prizes: any[]): string {
    if (prizes && prizes.length > 0) {
      return prizes.map(p => p.name).join(', ');
    }
    return '-';
  }

  // Location display helpers
  getLocationArray(item: any): string[] {
    const locationString = item.location || item.contest_location || '-';
    if (locationString === '-') return ['-'];
    return locationString.split(',').map((loc: string) => loc.trim());
  }

  getFirstTwoLocations(item: any): string {
    const locations = this.getLocationArray(item);
    return locations.slice(0, 2).join(', ');
  }

  getRemainingLocations(item: any): string {
    const locations = this.getLocationArray(item);
    return locations.slice(2).join(', ');
  }

  getRemainingLocationCount(item: any): number {
    const locations = this.getLocationArray(item);
    return locations.length - 2;
  }

  hasMoreThanTwoLocations(item: any): boolean {
    const locations = this.getLocationArray(item);
    return locations.length > 2;
  }

  // animate count 


  // Active tab handler
  setActiveTab(tab: 'all_contest' | 'completed' | 'draft') {
    this.tabs_grid = tab;

    // Reset page index when switching tabs
    this.pageIndex = 1;
    this.completedPageIndex = 1;
    this.draftPageIndex = 1;

    // Call API with appropriate status filter and current filters
    if (tab === 'all_contest') {
      this.getContestLists(1, '', this.currentFilters);
    }
    else if (tab === 'completed') {
      this.getContestLists(1, 'completed', this.currentFilters);
    }
    else if (tab === 'draft') {
      this.getContestLists(1, 'draft', this.currentFilters);
    }
  }


  // Methods add karein
  toggleShareMenu(event: Event, index: number): void {
    event.stopPropagation();

    if (this.activeShareMenuIndex === index) {
      this.activeShareMenuIndex = null;
    } else {
      this.activeShareMenuIndex = index;
    }
  }

  closeShareMenu(): void {
    this.activeShareMenuIndex = null;
  }

  showShareIcons(id: number): void {
    this.activeShareId = id;
  }

  hideShareIcons(): void {
    this.activeShareId = null;
  }

  private buildPublicContestShareUrl(contestId: number | string): string {
    const canonicalBaseUrl = 'https://luqta.io';
    return `${canonicalBaseUrl}/detailContestPage?navigationPage=homePage&contestId=${contestId}`;
  }

  shareOnSocial(event: Event, platform: string, contestData: any): void {
    event.stopPropagation();

    // Generate Luqta deep link URL
    const contestUrl = this.buildPublicContestShareUrl(contestData.id);
    const contestName = contestData.name || 'Contest';

    // Share message template
    const shareMessage = `🎉 The ${contestName} contest is now live on Luqta!\nJoin now and win exciting prizes — don't miss your chance!\n👉 ${contestUrl}`;

    switch (platform) {
      case 'twitter':
        const twitterText = `🎉 ${contestName} contest is now live on Luqta!\nJoin now and win exciting prizes! 🎁`;
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(contestUrl)}`,
          '_blank'
        );
        break;

      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
          '_blank'
        );
        break;

      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(contestUrl)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        const linkedinTitle = `Join the ${contestName} contest on Luqta!`;
        const linkedinSummary = `🎉 The ${contestName} contest is now live on Luqta! Join now and win exciting prizes — don't miss your chance!`;
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(contestUrl)}`,
          '_blank'
        );
        break;

      case 'copy':
        // Copy link to clipboard
        navigator.clipboard.writeText(contestUrl).then(() => {
          this.sharedService.createNotification('success', 'Contest link copied to clipboard!');
        }).catch(() => {
          this.sharedService.createNotification('error', 'Failed to copy link');
        });
        // Don't close menu on copy - let user share to social media after copying
        return;
    }

    // Close menu after sharing to social platforms
    this.closeShareMenu();
  }

  // Helper method to check if contest is shareable
  isContestShareable(contest: any): boolean {
    const status = (contest.status || contest.approval_status || '').toLowerCase();
    return status === 'approved' && !contest.isExpired;
  }

  getPercentageChange(current: number, previous: number): { value: number, isPositive: boolean } {
    if (previous === 0) {
      return { value: 0, isPositive: true };
    }
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0
    };
  }

  getTrendBadge(trend: number | null | undefined): { value: number, isPositive: boolean } {
    const safe = typeof trend === 'number' && isFinite(trend) ? trend : 0;
    return { value: Math.abs(safe), isPositive: safe >= 0 };
  }

  downloadContest() {
    const exportConfig = this.getExportConfigByTab();
    this.fetchAllContestsForExport(exportConfig.status, (contestsToExport) => {
      this.generateContestPDF(contestsToExport, exportConfig.reportTitle, exportConfig.fileName).catch(() => {
        this.sharedService.createNotification('error', 'Failed to generate PDF');
      });
    });
  }

  private formatExportDate(timestamp: any): string {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return 'N/A';
    }
  }

  private extractContestPrizes(contest: any): any[] {
    if (contest.prize && Array.isArray(contest.prize) && contest.prize.length > 0) {
      return contest.prize;
    }

    if (contest._contest_prize_of_contest_list && Array.isArray(contest._contest_prize_of_contest_list)) {
      return contest._contest_prize_of_contest_list;
    }

    if (contest._contest_prize && Array.isArray(contest._contest_prize)) {
      return contest._contest_prize;
    }

    if (contest.levels && Array.isArray(contest.levels)) {
      const nestedPrizes: any[] = [];
      contest.levels.forEach((level: any) => {
        if (level.prize && Array.isArray(level.prize)) {
          nestedPrizes.push(...level.prize);
        }
      });
      return nestedPrizes;
    }

    return [];
  }

  private getPdfLocationLabel(rawLocation: string): string {
    if (!rawLocation || rawLocation === 'N/A') {
      return 'N/A';
    }

    const countryList = Array.isArray(this.countryRecords) ? this.countryRecords : [];
    if (!countryList.length) {
      return rawLocation;
    }

    const normalizedLocation = rawLocation.toLowerCase();
    const uniqueCountryNames = Array.from(
      new Set(
        countryList
          .map((country: any) => String(country?.name || '').trim())
          .filter((name: string) => !!name)
      )
    );

    if (!uniqueCountryNames.length) {
      return rawLocation;
    }

    const allCountriesIncluded = uniqueCountryNames.every((countryName: string) =>
      normalizedLocation.includes(countryName.toLowerCase())
    );

    if (allCountriesIncluded) {
      return 'All';
    }

    return this.getExcelLocationLabel(rawLocation);
  }

  private getExcelLocationLabel(rawLocation: string): string {
    if (!rawLocation || rawLocation === 'N/A') {
      return 'N/A';
    }

    const countries = rawLocation
      .split(',')
      .map((country: string) => country.trim())
      .filter((country: string) => !!country);

    if (countries.length <= 5) {
      return rawLocation;
    }

    const firstFive = countries.slice(0, 5).join(', ');
    const remainingCount = countries.length - 5;
    return `${firstFive} +${remainingCount}`;
  }

  private buildContestExportData(
    contestsToExport: any[],
    collapseAllCountriesToAll: boolean = false,
    limitCountriesForExcel: boolean = false
  ): any[] {
    return contestsToExport.map((contest: any) => {
      const allPrizes = this.extractContestPrizes(contest);

      const prizeNames = allPrizes.length > 0
        ? allPrizes.map((p: any) => p.name || '').filter((n: string) => n).join(', ') || 'N/A'
        : 'N/A';

      const prizeValues = allPrizes.length > 0
        ? allPrizes
          .map((p: any) => p?.value ?? p?.prize_value ?? p?.amount ?? p?.prizeAmount)
          .filter((v: any) => v !== null && v !== undefined && String(v).trim() !== '')
          .map((v: any) => String(v))
          .join(', ') || 'N/A'
        : 'N/A';

      const levels = contest.levels || contest._levels_of_contest_list || contest.levels_of_contest || [];
      const levelCount = contest.level_count ?? (Array.isArray(levels) ? levels.length : 0);

      const isIntegrated = (contest.format_of_contest || '').toLowerCase() === 'integrated' ? 'Yes' : 'No';
      const rawLocation = contest.location || contest.contest_location || 'N/A';
      let location = collapseAllCountriesToAll
        ? this.getPdfLocationLabel(rawLocation)
        : rawLocation;

      if (limitCountriesForExcel) {
        location = this.getExcelLocationLabel(location);
      }

      return {
        'ID': contest.id || 'N/A',
        'Name': contest.name || 'N/A',
        'Start Date': this.formatExportDate(contest.start_date),
        'End Date': this.formatExportDate(contest.end_date),
        'Prize Announce Date': this.formatExportDate(contest.prize_announce_date || contest.prize_announcement_date),
        'Status': this.getStatusLabel(this.getDisplayStatus(contest)),
        'Type': contest.type || contest.contest_type || '-',
        'View Type': contest.View_Type || contest.view_type || contest.viewType || 'Normal',
        'Number of Levels': levelCount,
        'Number of Participants': contest.number_of_participants ?? contest.participants_count ?? contest.contest_participate ?? 0,
        'Access Code': contest.access_code || 'N/A',
        'Location': location,
        'Integrated': isIntegrated,
        'Prize Names': prizeNames,
        'Prize Values': prizeValues,
        'Created At': this.formatExportDate(contest.created_at),
        'Description': contest.description || contest.details || contest.arabic_details || 'N/A'
      };
    });
  }

  async generateContestPDF(contestsToExport: any[], reportTitle: string, fileName: string) {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const columns = [
      'ID',
      'Name',
      'Start Date',
      'End Date',
      'Prize Announce Date',
      'Status',
      'Type',
      'View Type',
      'Number of Levels',
      'Number of Participants',
      'Access Code',
      'Location',
      'Integrated',
      'Prize Names',
      'Prize Values',
      'Created At',
      'Description',
    ];

    const exportData = this.buildContestExportData(contestsToExport, true);
    const tableRows = exportData.map((row: any) => columns.map((column) => row[column] ?? 'N/A'));
    const horizontalMargin = 20; // left 10 + right 10
    const availableTableWidth = pageWidth - horizontalMargin;
    const baseColumnWidths = [8, 18, 14, 14, 16, 22, 10, 12, 10, 14, 12, 18, 10, 14, 12, 14, 23];
    const totalBaseWidth = baseColumnWidths.reduce((sum, width) => sum + width, 0);
    const widthScale = totalBaseWidth > 0 ? availableTableWidth / totalBaseWidth : 1;
    const scaledColumnStyles = baseColumnWidths.reduce((acc: any, width: number, index: number) => {
      acc[index] = { cellWidth: Math.max(7, Number((width * widthScale).toFixed(2))) };
      return acc;
    }, {});

    const shouldLoadArabicFont = tableRows.some((row: unknown[]) =>
      row.some((cell: unknown) => this.containsArabic(String(cell ?? '')))
    );
    const arabicFontLoaded = shouldLoadArabicFont ? await this.ensureArabicFont(doc) : false;

    // Generate autoTable
    autoTable(doc, {
      startY: 45,
      head: [columns],
      body: tableRows,
      styles: {
        fontSize: 6.5,
        cellPadding: 3,
        lineColor: [224, 224, 224],
        lineWidth: 0.1,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        fontSize: 6.5,
      },
      bodyStyles: {
        textColor: [85, 85, 85],
        halign: 'left',
        valign: 'middle',
        fillColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      margin: { top: 45, left: 10, right: 10, bottom: 20 },
      columnStyles: scaledColumnStyles,
      tableLineColor: [224, 224, 224],
      tableLineWidth: 0,
      didParseCell: (data) => {
        if (data.section !== 'body') return;

        // Keep status badge cell untouched; it is custom-drawn in didDrawCell.
        if (data.column.index === 5) return;

        const raw = String(data.cell.raw ?? '');
        if (!this.containsArabic(raw)) return;

        data.cell.styles.halign = 'right';
        data.cell.styles.fontSize = 7.5;
        if (!arabicFontLoaded) {
          data.cell.text = [''];
          return;
        }

        data.cell.styles.font = 'NotoNaskhArabic';
        const processArabic = (doc as any).processArabic;
        data.cell.text = [
          typeof processArabic === 'function' ? processArabic(raw) : raw
        ];
      },
      didDrawPage: function (data) {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, 42, 'F');

        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        doc.text(reportTitle, pageWidth / 2, 15, {
          align: 'center',
        });

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        doc.text(`Total Contests:`, 14, 28);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(85, 85, 85);
        doc.text(tableRows.length.toString(), 50, 28);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        doc.text(`Generated Date:`, 14, 35);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(85, 85, 85);
        doc.text(new Date().toLocaleDateString('en-GB'), 50, 35);

        doc.setDrawColor(224, 224, 224);
        doc.setLineWidth(0.5);
        doc.line(14, 40, pageWidth - 14, 40);

        const pageCount = doc.internal.pages.length - 1;
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth - 14,
          pageHeight - 10,
          { align: 'right' }
        );
      },
      didDrawCell: (data) => {
        // Fallback path for Arabic text when Unicode font could not be loaded.
        if (!arabicFontLoaded && data.section === 'body' && data.column.index !== 5) {
          const raw = String(data.cell.raw ?? '');
          if (this.containsArabic(raw)) {
            this.drawArabicCellAsImage(doc, raw, data.cell.x, data.cell.y, data.cell.width, data.cell.height);
          }
        }

        // STATUS BADGE
        if (data.section === 'body' && data.column.index === 5) {
          const statusValue = data.cell.raw;
          const status = typeof statusValue === 'string' ? statusValue : String(statusValue || 'N/A');
          const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');

          let bgColor: number[], textColor: number[];

          switch (normalizedStatus) {
            case 'approved':
              bgColor = [212, 237, 218];
              textColor = [21, 87, 36];
              break;
            case 'pending':
              bgColor = [255, 236, 179];
              textColor = [134, 100, 0];
              break;
            case 'completed':
              bgColor = [217, 245, 232];
              textColor = [16, 122, 78];
              break;
            case 'draft':
              bgColor = [226, 227, 229];
              textColor = [56, 61, 65];
              break;
            case 'ongoing':
              bgColor = [210, 229, 255];
              textColor = [22, 95, 168];
              break;
            case 'waiting_prize':
              bgColor = [235, 220, 250];
              textColor = [104, 57, 145];
              break;
            default:
              bgColor = [245, 245, 245];
              textColor = [85, 85, 85];
          }

          const padding = 2;
          const badgeWidth = data.cell.width - (padding * 2);
          const badgeHeight = 6;
          const badgeX = data.cell.x + padding;
          const badgeY = data.cell.y + (data.cell.height - badgeHeight) / 2;

          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 1.5, 1.5, 'F');

          doc.setTextColor(textColor[0], textColor[1], textColor[2]);
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'bold');
          doc.text(status, data.cell.x + data.cell.width / 2, badgeY + 4.5, {
            align: 'center'
          });
        }

        // Horizontal lines only
        if (data.section === 'body') {
          doc.setDrawColor(224, 224, 224);
          doc.setLineWidth(0.1);
          doc.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          );
        }
      }
    });

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`${fileName}_${timestamp}.pdf`);
    this.sharedService.createNotification('success', 'PDF downloaded successfully');
  }

  private drawArabicCellAsImage(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    const canvas = document.createElement('canvas');
    const scale = 2;
    const targetWidthPx = 220;
    const targetHeightPx = 64;

    canvas.width = targetWidthPx * scale;
    canvas.height = targetHeightPx * scale;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, targetWidthPx, targetHeightPx);
    ctx.fillStyle = '#000000';
    ctx.direction = 'rtl';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    let fontPx = 28;
    const minFontPx = 16;
    const maxTextWidth = targetWidthPx - 10;

    while (fontPx > minFontPx) {
      ctx.font = `500 ${fontPx}px "Segoe UI", Tahoma, Arial, sans-serif`;
      if (ctx.measureText(text).width <= maxTextWidth) break;
      fontPx -= 1;
    }

    ctx.font = `500 ${fontPx}px "Segoe UI", Tahoma, Arial, sans-serif`;
    ctx.fillText(text, targetWidthPx - 5, targetHeightPx / 2);

    const imgData = canvas.toDataURL('image/png');
    const imgTargetHeight = Math.min(5.2, Math.max(4.0, height - 1.5));
    const imgTargetWidth = Math.min(
      width - 2,
      (targetWidthPx / targetHeightPx) * imgTargetHeight
    );
    const imgX = x + width - imgTargetWidth - 1;
    const imgY = y + (height - imgTargetHeight) / 2;

    doc.addImage(imgData, 'PNG', imgX, imgY, imgTargetWidth, imgTargetHeight, undefined, 'FAST');
  }

  private containsArabic(value: string): boolean {
    return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(value);
  }

  private async ensureArabicFont(doc: jsPDF): Promise<boolean> {
    try {
      const fontName = 'NotoNaskhArabic';
      const fontFile = 'NotoNaskhArabic-Regular.ttf';
      const fontList = doc.getFontList?.() as Record<string, unknown> | undefined;

      if (fontList && fontList[fontName]) {
        return true;
      }

      const response = await fetch(
        'https://raw.githubusercontent.com/google/fonts/main/ofl/notonaskharabic/NotoNaskhArabic-Regular.ttf'
      );
      if (!response.ok) return false;

      const fontBuffer = await response.arrayBuffer();
      const fontBase64 = this.arrayBufferToBase64(fontBuffer);

      doc.addFileToVFS(fontFile, fontBase64);
      doc.addFont(fontFile, fontName, 'normal');
      return true;
    } catch {
      return false;
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  }


  // Excel Export ke liye complete function

// Excel Download Main Function
downloadExcel() {
  const exportConfig = this.getExportConfigByTab();
  this.fetchAllContestsForExport(exportConfig.status, (contestsToExport) => {
    this.generateContestExcel(contestsToExport, exportConfig.fileName);
  });
}

// Excel Generate Function with Date Formatting
generateContestExcel(contestsToExport: any[], fileName: string) {
  const excelData = this.buildContestExportData(contestsToExport, false, true);

  // Worksheet banao
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Column widths set karo
  worksheet['!cols'] = [
    { wch: 10 },  // ID
    { wch: 30 },  // Name
    { wch: 20 },  // Start Date
    { wch: 20 },  // End Date
    { wch: 20 },  // Prize Announce Date
    { wch: 15 },  // Status
    { wch: 15 },  // Type
    { wch: 15 },  // View Type
    { wch: 15 },  // Number of Levels
    { wch: 20 },  // Number of Participants
    { wch: 15 },  // Access Code
    { wch: 25 },  // Location
    { wch: 12 },  // Integrated
    { wch: 30 },  // Prize Names
    { wch: 20 },  // Prize Values
    { wch: 20 },  // Created At
    { wch: 40 }   // Description
  ];

  // Workbook banao
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Contests');

  // Excel file ko array buffer mein convert karo
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
  // Blob banao
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });

  // File name with timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  
  // File save karo
  saveAs(blob, `${fileName}_${timestamp}.xlsx`);
  
  // Success notification
  this.sharedService.createNotification('success', 'Excel downloaded successfully');
}

  // Date format karne ka helper function
  formatDate(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format: DD/MM/YYYY HH:mm
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  }

  // Alternative: Agar sirf date chahiye (without time)
  formatDateOnly(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date';

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  // Image Modal Methods
  openImageModal(images: any[], startIndex: number = 0): void {
    // Convert banner objects to URL strings
    this.imageModalImages = images
      .filter(img => img?.url)
      .map(img => img.url);

    if (this.imageModalImages.length > 0) {
      this.imageModalCurrentIndex = startIndex;
      this.imageModalVisible = true;
    }
  }

  openSingleImage(imageUrl: string): void {
    if (imageUrl) {
      this.imageModalImages = [imageUrl];
      this.imageModalCurrentIndex = 0;
      this.imageModalVisible = true;
    }
  }

  closeImageModal(): void {
    this.imageModalVisible = false;
    this.imageModalImages = [];
    this.imageModalCurrentIndex = 0;
  }

  onImageIndexChange(index: number): void {
    this.imageModalCurrentIndex = index;
  }
}
